import { Button } from '@kcftech/design-system';
import { Checkbox } from 'components/forms/Checkbox';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { useHighlightedIndex } from 'custom_hooks/useHighlightedIndex';
import { useKeyPress } from 'custom_hooks/useKeyPress';
import { useKeyPressed } from 'custom_hooks/useKeyPressed';
import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { toggleArrayItem } from 'utils/array';

const PopupContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    overflow: auto;
`;

const Label = styled.label`
    display: inline-flex;
    gap: 0.4em;
    cursor: pointer;
    align-items: center;

    padding-left: 0.2rem;
    padding-right: 0.2rem;
    border-radius: 0.5rem;
    border: 1px solid transparent;
    &:hover,
    &:focus-visible,
    &.highlighted {
        border-color: var(--action-hover);
    }
    &:active,
    &.active {
        background-color: var(--action-selected);
    }
`;

const LabelContentWrapper = styled.span`
    display: inline-flex;
    justify-content: space-between;
    gap: 4rem;
    flex-grow: 1;

    --p: 0.28rem;
    padding-top: var(--p);
    padding-bottom: var(--p);
`;

export type MultiSelectQuickFilterOption<TValue> = {
    value: TValue;
    /**
     * Label can be a string or ReactNode (e.g., a Lozenge component)
     */
    label: ReactNode;
    /**
     * Optional extra content to display on the right side of the label
     * (e.g., counts, badges, etc.)
     */
    extra?: ReactNode;
};

export type MultiSelectQuickFilterPopupProps<TValue> = {
    close: () => void;
    maxHeight: string;
    selectedValues: TValue[];
    onChangeValues: (values: TValue[]) => void;
    popupRef: React.RefObject<HTMLDivElement>;
    triggerRef: React.RefObject<HTMLButtonElement>;
    /**
     * All available options to display in the popup.
     * Should already be filtered and sorted as desired.
     */
    options: MultiSelectQuickFilterOption<TValue>[];
};

export function MultiSelectQuickFilterPopup<TValue>({
    close,
    maxHeight,
    selectedValues,
    onChangeValues,
    popupRef,
    triggerRef,
    options,
}: MultiSelectQuickFilterPopupProps<TValue>) {
    useKeyPress('Escape', () => {
        close();
    });

    const selectedValuesSet = new Set(selectedValues);
    const isSelected = (value: TValue) => selectedValuesSet.has(value);

    const onChange = (value: TValue) =>
        onChangeValues(toggleArrayItem(selectedValues, value));

    const [highlightedIndex, setHighlightedIndex] = useState<number>();

    const keyHandler = useHighlightedIndex({
        items: options,
        highlightedIndex: highlightedIndex ?? -1,
        onChange: (idx) => {
            setHighlightedIndex(idx);

            const el = document.getElementById(`opt-${idx}`);
            if (!el) {
                return;
            }

            el.scrollIntoView({
                behavior: 'instant',
                block: 'nearest',
                inline: 'nearest',
            });
            el.focus();
        },
        onSelect: (item) => {
            onChange(item.value);
        },
        spaceSubmits: true,
    });

    useKeyPress(['ArrowUp', 'ArrowDown', 'Enter'], keyHandler);
    const isEnterPressed = useKeyPressed('Enter');

    return (
        <StatelessPopup
            ref={popupRef}
            onClose={close}
            anchor={triggerRef.current}
            collision={{
                horizontal: 'fit',
                vertical: 'none',
            }}
            popupSettings={{}}
            noOverlay
            noPadding
        >
            <div
                style={{
                    maxHeight,
                    padding: '1rem',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Button
                    className="mb-5"
                    appearance="secondary"
                    spacing="compact"
                    disabled={selectedValues.length === 0}
                    onClick={() => onChangeValues([])}
                >
                    Clear
                </Button>
                <PopupContentWrapper>
                    {options.map((option, idx) => (
                        <Label
                            key={idx}
                            id={`opt-${idx}`}
                            className={
                                highlightedIndex === idx
                                    ? 'highlighted' + (isEnterPressed ? ' active' : '')
                                    : ''
                            }
                            onMouseEnter={() => {
                                setHighlightedIndex(idx);
                            }}
                        >
                            <Checkbox
                                tabIndex={-1}
                                checked={isSelected(option.value)}
                                aria-selected={
                                    highlightedIndex === idx ? 'true' : undefined
                                }
                                label={
                                    <LabelContentWrapper>
                                        <span>{option.label}</span>
                                        {option.extra && <span>{option.extra}</span>}
                                    </LabelContentWrapper>
                                }
                                onChange={() => onChange(option.value)}
                                onBlur={(e) => {
                                    if (
                                        e.relatedTarget &&
                                        e.relatedTarget !== triggerRef.current &&
                                        !popupRef.current?.contains(e.relatedTarget)
                                    ) {
                                        close();
                                    }
                                }}
                            />
                        </Label>
                    ))}
                </PopupContentWrapper>
            </div>
        </StatelessPopup>
    );
}
